// Table density preference — comfortable (44 px rows) or compact (36 px rows).
// Persisted in localStorage; the default follows the screen (compact on ≥1920
// control-room displays, comfortable on laptops). AppShell mirrors the value
// onto <html data-density> so tokens.css can drive --pb-row-h, and binds ⌥D as
// the global toggle. Settings → Appearance exposes the same store.

import { useCallback, useSyncExternalStore } from "react";

export type Density = "comfortable" | "compact";

const STORAGE_KEY = "pb.density";
const BIG_SCREEN_PX = 1920;

const listeners = new Set<() => void>();

function readStored(): Density | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "compact" || v === "comfortable" ? v : null;
  } catch {
    return null;
  }
}

/** The density the screen would pick on its own (no stored preference). */
export function autoDensity(): Density {
  if (typeof window === "undefined") return "comfortable";
  return window.innerWidth >= BIG_SCREEN_PX ? "compact" : "comfortable";
}

export function getDensity(): Density {
  return readStored() ?? autoDensity();
}

/** True when the user has pinned a density (vs. following the screen). */
export function isDensityPinned(): boolean {
  return readStored() !== null;
}

function emit(): void {
  listeners.forEach((l) => l());
}

export function setDensity(d: Density | null): void {
  try {
    if (d === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, d);
  } catch {
    /* private mode — in-memory only for this tab */
  }
  applyDensity();
  emit();
}

export function toggleDensity(): Density {
  const next: Density = getDensity() === "compact" ? "comfortable" : "compact";
  setDensity(next);
  return next;
}

/** Mirror the current density onto <html> so CSS tokens can react. */
export function applyDensity(): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.density = getDensity();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  // The auto default depends on the viewport; re-evaluate on resize only when
  // nothing is pinned.
  const onResize = () => {
    if (!isDensityPinned()) {
      applyDensity();
      cb();
    }
  };
  window.addEventListener("resize", onResize);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("resize", onResize);
  };
}

export function useDensity(): {
  density: Density;
  pinned: boolean;
  set: (d: Density | null) => void;
  toggle: () => void;
} {
  const density = useSyncExternalStore(subscribe, getDensity, () => "comfortable" as Density);
  const pinned = useSyncExternalStore(subscribe, isDensityPinned, () => false);
  const set = useCallback((d: Density | null) => setDensity(d), []);
  const toggle = useCallback(() => {
    toggleDensity();
  }, []);
  return { density, pinned, set, toggle };
}
